import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Search, Sparkles, X } from "lucide-react";
import { COLORPLAN } from "./ColorplanSwatches";
import { findFoil } from "../lib/foilChart";
import { contrastRatio, isLightHex } from "../lib/foilPairings";
import { FoilOnPaper } from "./foil/FoilPairings";

export const COLORPLAN_RECOMMEND = "Kolt recommends";
const tid = (n) => n.toLowerCase().replace(/\s+/g, "-");

// Colorplan shade picker — shown on products with `colorplan_picker_enabled` while a
// Colorplan material is selected and the foil picker is active. Optional for the buyer.
export function ColorplanShadePicker({ value, onChange, foilCode, surchargePerPiece = 0 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const foil = findFoil(foilCode);
  const selected = COLORPLAN.find(([n]) => n === value);
  const query = q.trim().toLowerCase();
  const list = useMemo(() => {
    const rows = COLORPLAN.filter(([n]) => !query || n.toLowerCase().includes(query)).map(([name, hex]) => ({ name, hex, contrast: foil ? contrastRatio(foil.hex, hex) : 0 }));
    return foil ? rows.sort((a, b) => b.contrast - a.contrast) : rows;
  }, [query, foil]);
  const fee = Number(surchargePerPiece) > 0 ? `+$${Number(surchargePerPiece).toFixed(2)}/pc` : "";

  return (
    <div className="mt-5" data-testid="colorplan-shade-picker">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="overline">Colorplan shade {fee && <span className="text-kolt-orange normal-case tracking-normal font-sans font-bold ml-1" data-testid="colorplan-fee">{fee}</span>}</div>
        <Link to="/tools/colorplan" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 hover:text-kolt-orange" data-testid="colorplan-picker-guide-link">All 55 shades <ArrowUpRight className="w-3 h-3" /></Link>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => { onChange(""); setOpen(false); }} className={`text-left border p-3 transition-colors ${!selected ? "border-kolt-slate-dark bg-kolt-slate-dark text-white" : "border-slate-300 bg-white hover:border-[#B87333]"}`} data-testid="colorplan-picker-recommend">
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase font-bold"><Sparkles className="w-3.5 h-3.5 text-[#B87333]" /> {COLORPLAN_RECOMMEND}</div>
          <div className={`text-[11px] mt-1 leading-snug ${!selected ? "text-white/70" : "text-slate-500"}`}>We propose a Colorplan shade to suit your foil and artwork at proof stage.</div>
        </button>
        <button type="button" onClick={() => setOpen((o) => !o)} className={`text-left border p-3 transition-colors ${selected ? "border-[#B87333] bg-white ring-2 ring-[#B87333]/30" : "border-slate-300 bg-white hover:border-[#B87333]"}`} aria-expanded={open} data-testid="colorplan-picker-choose">
          {selected ? (
            <div className="flex items-center gap-3">
              {foil ? <FoilOnPaper foil={foil} paperHex={selected[1]} className="w-10 h-10 border border-slate-300 shrink-0" /> : <span className="w-10 h-10 border border-slate-300 shrink-0" style={{ backgroundColor: selected[1] }} />}
              <div className="min-w-0">
                <div className="font-display font-black text-sm text-kolt-slate-dark truncate" data-testid="colorplan-picker-selected-name">{selected[0]}</div>
                <div className="text-[10px] text-slate-500">Colorplan · tap to change</div>
              </div>
            </div>
          ) : (
            <>
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase font-bold text-kolt-slate-dark">Choose a shade</div>
              <div className="text-[11px] text-slate-500 mt-1 leading-snug">55 GF Smith Colorplan colours{foil ? `, ranked by contrast with foil ${foil.code}` : ""}.</div>
            </>
          )}
        </button>
      </div>

      {open && (
        <div className="mt-2 border border-slate-300 bg-white p-3" data-testid="colorplan-picker-panel">
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a shade — Ebony, Imperial Blue…" className="w-full border border-slate-300 focus:border-[#B87333] outline-none pl-8 pr-2 py-2 text-xs" data-testid="colorplan-picker-search" aria-label="Search Colorplan shades" />
            </div>
            <button type="button" onClick={() => setOpen(false)} className="p-2 text-slate-400 hover:text-kolt-slate-dark" aria-label="Close shade picker" data-testid="colorplan-picker-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-72 overflow-y-auto pr-1" data-testid="colorplan-picker-grid">
            {list.map((p) => (
              <button key={p.name} type="button" onClick={() => { onChange(p.name); setOpen(false); }} className={`group relative text-left border transition-colors ${value === p.name ? "border-[#B87333] ring-2 ring-[#B87333]/30" : "border-slate-200 hover:border-[#B87333]"}`} title={foil ? `${p.name} · contrast ${p.contrast.toFixed(1)}:1` : p.name} data-testid={`colorplan-picker-option-${tid(p.name)}`}>
                {foil ? <FoilOnPaper foil={foil} paperHex={p.hex} className="aspect-square" /> : <div className="aspect-square" style={{ backgroundColor: p.hex }} />}
                {value === p.name && <span className={`absolute top-1 right-1 w-4 h-4 grid place-items-center ${isLightHex(p.hex) ? "bg-kolt-slate-dark text-white" : "bg-white text-kolt-slate-dark"}`}><Check className="w-3 h-3" /></span>}
                <div className="px-1 py-1 text-[9px] font-bold text-kolt-slate-dark truncate leading-tight">{p.name}</div>
              </button>
            ))}
            {!list.length && <div className="col-span-full text-xs text-slate-500 py-4 text-center" data-testid="colorplan-picker-empty">No shade matches “{q}”.</div>}
          </div>
        </div>
      )}

      {selected && (
        <div className="mt-2 text-[11px] text-slate-600" data-testid="colorplan-picker-note">
          Stock: <b>Colorplan {selected[0]}</b>{foil ? <> with foil <b>{foil.code}</b></> : null} — the pairing prints on your order and proof.
        </div>
      )}
    </div>
  );
}
